import { 
  getFirestore, 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  Timestamp 
} from 'firebase/firestore';

const db = getFirestore();

class SessionDeduplicationService {
  constructor() {
    this.isRunning = false;
    this.deduplicationInterval = null;
  }

  // Start the deduplication service
  start() {
    if (this.isRunning) return;
    
    
    this.isRunning = true;
    
    // Run immediately
    this.deduplicateSessions();
    
    // Run every 5 minutes
    this.deduplicationInterval = setInterval(() => {
      this.deduplicateSessions();
    }, 5 * 60 * 1000);
  }

  // Stop the deduplication service
  stop() {
    if (!this.isRunning) return;
    
    
    this.isRunning = false;
    
    if (this.deduplicationInterval) {
      clearInterval(this.deduplicationInterval);
      this.deduplicationInterval = null;
    }
  }

  // Main deduplication logic
  async deduplicateSessions() {
    try {
      // Remove duplicate sessions
      await this.removeDuplicateSessions();
      
      // Clean up stale sessions
      await this.cleanupStaleSessions();
      
      // Validate session data
      await this.validateSessionData();
      
    } catch (error) {
      // If permission denied, stop the service to avoid spamming errors
      if (error?.code === 'permission-denied' || error?.message?.includes('Missing or insufficient permissions')) {
        console.log('Session deduplication: insufficient permissions, stopping service for this session.');
        this.stop();
      }
    }
  }

  // Remove duplicate sessions based on user/visitor and timeframe
  async removeDuplicateSessions() {
    try {
      const now = new Date();
      const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
      
      // Get sessions from the last hour
      let q;
      try {
        q = query(
          collection(db, 'sessions_v2'),
          where('startTime', '>', Timestamp.fromDate(oneHourAgo))
        );
      } catch (e) {
        return; // Can't build query, skip
      }
      
      const snapshot = await getDocs(q);
      const sessionsByUser = new Map();
      const sessionsByVisitor = new Map();
      
      // Group sessions by user ID and visitor ID
      snapshot.forEach((doc) => {
        const data = doc.data();
        const sessionDoc = { id: doc.id, ...data };
        
        // Group by user ID for authenticated users
        if (data.userId && data.userId !== 'anonymous') {
          if (!sessionsByUser.has(data.userId)) {
            sessionsByUser.set(data.userId, []);
          }
          sessionsByUser.get(data.userId).push(sessionDoc);
        }
        
        // Group by visitor ID for all users
        if (data.visitorId) {
          if (!sessionsByVisitor.has(data.visitorId)) {
            sessionsByVisitor.set(data.visitorId, []);
          }
          sessionsByVisitor.get(data.visitorId).push(sessionDoc);
        }
      });
      
      // Process duplicates for authenticated users
      for (const [userId, sessions] of sessionsByUser) {
        if (sessions.length > 1) {
          await this.processDuplicateSessions(sessions, 'user', userId);
        }
      }
      
      // Process duplicates for visitors (more lenient)
      for (const [visitorId, sessions] of sessionsByVisitor) {
        if (sessions.length > 2) { // Allow up to 2 sessions per visitor
          await this.processDuplicateSessions(sessions, 'visitor', visitorId);
        }
      }
    } catch (error) {
      // Re-throw permission errors so the main handler can stop the service
      if (error?.code === 'permission-denied' || error?.message?.includes('Missing or insufficient permissions')) {
        throw error;
      }
    }
  }

  // Process duplicate sessions by keeping the most recent/active one
  async processDuplicateSessions(sessions, type, identifier) {
    try {
      // Sort sessions by start time (newest first)
      sessions.sort((a, b) => {
        const aTime = a.startTime?.toDate?.() || new Date(a.startTime);
        const bTime = b.startTime?.toDate?.() || new Date(b.startTime);
        return bTime - aTime;
      });
      
      // Keep the most recent active session, or just the most recent if none are active
      let sessionToKeep = sessions.find(s => s.isActive) || sessions[0];
      
      // Remove the others
      for (const session of sessions) {
        if (session.id !== sessionToKeep.id) {
          try {
            await deleteDoc(doc(db, 'sessions_v2', session.id));
            
          } catch (error) {
            
          }
        }
      }
    } catch (error) {
      
    }
  }

  // Clean up stale sessions that are marked as active but haven't been updated
  async cleanupStaleSessions() {
    try {
      const now = new Date();
      const staleThreshold = new Date(now.getTime() - 60 * 60 * 1000); // 1 hour
      
      const q = query(
        collection(db, 'sessions_v2'),
        where('isActive', '==', true),
        where('lastActivity', '<', Timestamp.fromDate(staleThreshold))
      );
      
      const snapshot = await getDocs(q);
      
      for (const docSnapshot of snapshot.docs) {
        try {
          const data = docSnapshot.data();
          const sessionDuration = now - (data.startTime?.toDate?.() || new Date(data.startTime));
          
          // Mark session as ended
          await updateDoc(doc(db, 'sessions_v2', docSnapshot.id), {
            isActive: false,
            endTime: Timestamp.fromDate(now),
            duration: sessionDuration
          });
        } catch (error) {
          // Skip individual doc errors silently
        }
      }
    } catch (error) {
      if (error?.code === 'permission-denied' || error?.message?.includes('Missing or insufficient permissions')) {
        throw error;
      }
    }
  }

  // Validate session data integrity
  async validateSessionData() {
    try {
      const now = new Date();
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      
      // Get recent sessions for validation
      const q = query(
        collection(db, 'sessions_v2'),
        where('startTime', '>', Timestamp.fromDate(oneDayAgo))
      );
      
      const snapshot = await getDocs(q);
      
      for (const docSnapshot of snapshot.docs) {
        try {
          const data = docSnapshot.data();
          let needsUpdate = false;
          const updates = {};
          
          // Validate duration
          if (data.endTime && (!data.duration || data.duration < 0)) {
            const startTime = data.startTime?.toDate?.() || new Date(data.startTime);
            const endTime = data.endTime?.toDate?.() || new Date(data.endTime);
            updates.duration = endTime - startTime;
            needsUpdate = true;
          }
          
          // Validate page views count
          if (!data.pageViews || data.pageViews < 0) {
            updates.pageViews = (data.pages?.length || 0);
            needsUpdate = true;
          }
          
          // Ensure pages array exists
          if (!Array.isArray(data.pages)) {
            updates.pages = [];
            needsUpdate = true;
          }
          
          // Validate visitor ID presence
          if (!data.visitorId) {
            updates.visitorId = `validated_${data.userId || 'anonymous'}_${Date.now()}`;
            needsUpdate = true;
          }
          
          if (needsUpdate) {
            try {
              await updateDoc(doc(db, 'sessions_v2', docSnapshot.id), updates);
              
            } catch (updateError) {
              if (updateError.code === 'not-found') {
                
              } else {
                throw updateError;
              }
            }
          }
        } catch (error) {
          // Skip individual doc errors silently
        }
      }
    } catch (error) {
      if (error?.code === 'permission-denied' || error?.message?.includes('Missing or insufficient permissions')) {
        throw error;
      }
    }
  }

  // Get deduplication statistics
  async getDeduplicationStats() {
    try {
      const now = new Date();
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      
      // Get session counts
      const allSessionsQ = query(
        collection(db, 'sessions_v2'),
        where('startTime', '>', Timestamp.fromDate(oneDayAgo))
      );
      
      const activeSessionsQ = query(
        collection(db, 'sessions_v2'),
        where('isActive', '==', true),
        where('startTime', '>', Timestamp.fromDate(oneDayAgo))
      );
      
      const [allSessions, activeSessions] = await Promise.all([
        getDocs(allSessionsQ),
        getDocs(activeSessionsQ)
      ]);
      
      // Count unique users and visitors
      const uniqueUsers = new Set();
      const uniqueVisitors = new Set();
      
      allSessions.forEach((doc) => {
        const data = doc.data();
        if (data.userId && data.userId !== 'anonymous') {
          uniqueUsers.add(data.userId);
        }
        if (data.visitorId) {
          uniqueVisitors.add(data.visitorId);
        }
      });
      
      return {
        totalSessions: allSessions.size,
        activeSessions: activeSessions.size,
        uniqueUsers: uniqueUsers.size,
        uniqueVisitors: uniqueVisitors.size,
        lastCleanup: now
      };
    } catch (error) {
      
      return {
        totalSessions: 0,
        activeSessions: 0,
        uniqueUsers: 0,
        uniqueVisitors: 0,
        lastCleanup: now,
        error: error.message
      };
    }
  }
}

// Export singleton instance
export const sessionDeduplicationService = new SessionDeduplicationService();
export default sessionDeduplicationService;